import writeXlsxFile from "write-excel-file";
import JSZip from "jszip";
import { saveAs } from "file-saver";

import { AdminRoles, AvatarCount, FitnessLevel, ManualEntry, Phase, WorkoutType } from "../config/DiactiveTypes";
import { getPatientGroupName, timestampToLocalDate, getSessionWeekNumber, getPatientHspId, calculatePostEvalDate } from "../utils/DiactiveUtils";
import { getHospitalName } from "../services/HospitalService";
import { getArrowString, getArrowTypeName } from '../utils/TrendArrowUtils';
import { getPatientData } from "../services/PatientService";
import { getUserDevices } from "../services/UserService";
import { calculateWorkoutStats, fetchWorkouts } from "../services/WorkoutService";
import { fetchQuestionnairesForExport } from "../services/QuestionnaireService";
import { getPatientIncidents } from "../services/IncidentService";

import PatientExcelConfig from "../data/PatientExcelConfig.json";
import WorkoutExcelConfig from "../data/WorkoutExcelConfig.json";
import QuestionnaireExcelConfig from "../data/QuestionnaireExcelConfig.json";
import exercisesJson from "./exercises.json";

const singleUserSheetName = "Datos de usuario";
const userWorkoutsSheetName = "Historial de sesiones";
const multiUsersSheetName = "Usuarios";
const multiWorkoutsSheetName = "Sesiones";
const questionnairesSheetName = "Cuestionarios";

// Questionnaire answers are only included for admins, matching the admin-only "Excel de Cuestionarios" export.
const canExportQuestionnaires = (role) => AdminRoles.includes(role);

const fetchPatientExportData = async (role, userId) => {
  const includeQuestionnaires = canExportQuestionnaires(role);
  const [patientData, userDevices, workouts, incidents, questionnaires] = await Promise.all([
    getPatientData(role, userId),
    getUserDevices(userId),
    fetchWorkouts(userId),
    getPatientIncidents(userId),
    includeQuestionnaires ? fetchQuestionnairesForExport(userId) : null,
  ]);
  const context = {
    userDevices,
    workoutStats: calculateWorkoutStats(workouts),
    hasIncidents: incidents.length > 0,
    questionnaires,
  };
  return { ...patientData, context, workouts };
};

export const exportMultiUsersFile = async (role, users, onProgress) => {
  const includeQuestionnaires = canExportQuestionnaires(role);
  const usersTable = [
    generateUserHeaderRow(includeQuestionnaires)
  ];
  let count = 0;
  for (const user of users) {
    onProgress(count, users.length);
    const { userData, preEvalData, postEvalData, context } = await fetchPatientExportData(role, user.id);
    const evalRows = generateUserEvalRows(userData, preEvalData, postEvalData, context);
    usersTable.push(...evalRows);
    count++;
  }

  const userColumns = generateUserColumns(includeQuestionnaires);

  const fileName = `${multiUsersSheetName}.xlsx`;
  const result = await writeXlsxFile([usersTable], {
    columns: [userColumns],
    sheets: [multiUsersSheetName],
    fileName: fileName
  });
  return result;
};

export const exportMultiWorkoutsFile = async (role, users, onProgress) => {
  const workoutsTable = [
    generateWorkoutsHeaderRow(true)
  ];
  let count = 0;
  for (const user of users) {
    onProgress(count, users.length);
    const { userData } = await getPatientData(role, user.id);
    const workouts = await fetchWorkouts(user.id);
    const workoutRows = generateUserWorkoutRows(userData, workouts, true);
    workoutsTable.push(...workoutRows);
    count++;
  }

  const workoutColumns = generateWorkoutColumns(true);

  const fileName = `${multiWorkoutsSheetName}.xlsx`;
  const result = await writeXlsxFile([workoutsTable], {
    columns: [workoutColumns],
    sheets: [multiWorkoutsSheetName],
    fileName: fileName
  });
  return result;
};

export const exportQuestionnairesFile = async (role, users, onProgress) => {
  const questionnairesTable = [
    generateQuestionnairesHeaderRow()
  ];
  let count = 0;
  for (const user of users) {
    onProgress(count, users.length);
    const { userData } = await getPatientData(role, user.id);
    const questionnaires = await fetchQuestionnairesForExport(user.id);
    const questionnaireRows = generateUserQuestionnaireRows(userData, questionnaires);
    questionnairesTable.push(...questionnaireRows);
    count++;
  }

  const questionnaireColumns = generateQuestionnaireColumns();

  const fileName = `${questionnairesSheetName}.xlsx`;
  const result = await writeXlsxFile([questionnairesTable], {
    columns: [questionnaireColumns],
    sheets: [questionnairesSheetName],
    fileName: fileName
  });
  return result;
};

export const exportSingleUsersZip = async (role, users, onProgress) => {
  const zip = new JSZip();
  let count = 0;
  for (const user of users) {
    onProgress(count, users.length);
    const blob = await exportSingleUserFile(role, user.id, true);
    const fileName = `${user.nickname}.xlsx`;
    zip.file(fileName, blob);
    count++;
  }
  const zipBlob = await zip.generateAsync({ type: "blob" });
  saveAs(zipBlob, "Datos_Usuarios_Diactive.zip");
};

export const exportSingleUserFile = async (role, userId, returnBlob) => {
  const { userData, preEvalData, postEvalData, context, workouts } = await fetchPatientExportData(role, userId);
  const result = await generateSingleUserFile(userData, preEvalData, postEvalData, context, workouts, returnBlob);
  return result;
};

const generateSingleUserFile = async (userData, preEvalData, postEvalData, context, workouts, returnBlob) => {
  const { userTable, userColumns } = generateSingleUserSheet(userData, preEvalData, postEvalData, context);
  const { workoutsTable, workoutsColumns } = generateUserWorkoutsSheet(userData, workouts);

  const fileName = `${userData.nickname}.xlsx`;
  const result = await writeXlsxFile([userTable, workoutsTable], {
    columns: [userColumns, workoutsColumns],
    sheets: [singleUserSheetName, userWorkoutsSheetName],
    fileName: returnBlob ? null : fileName,
  });
  return result;
};

const generateSingleUserSheet = (userData, preEvalData, postEvalData, context) => {
  const includeQuestionnaires = context.questionnaires !== null;
  const userHeaderRow = generateUserHeaderRow(includeQuestionnaires);
  const evalRows = generateUserEvalRows(userData, preEvalData, postEvalData, context);
  const userTable = [userHeaderRow, ...evalRows];
  return { userTable, userColumns: generateUserColumns(includeQuestionnaires) };
};

const generateUserEvalRows = (userData, preEvalData, postEvalData, context) => {
  const preEvalRow = generateUserEvalRow(userData, preEvalData, context, Phase.PRE);

  let postEvalRow = null;
  if (Object.keys(postEvalData).length > 0) {
    postEvalRow = generateUserEvalRow(userData, postEvalData, context, Phase.POST);
  }

  const evalRows = [preEvalRow];
  if (postEvalRow) evalRows.push(postEvalRow);
  return evalRows;
};

const generateUserWorkoutsSheet = (userData, workouts) => {
  const workoutsHeaderRow = generateWorkoutsHeaderRow(false);
  const workoutRows = generateUserWorkoutRows(userData, workouts, false);
  const workoutsTable = [workoutsHeaderRow, ...workoutRows];
  return { workoutsTable, workoutsColumns: generateWorkoutColumns(false) };
};

const generateUserWorkoutRows = (userData, workouts, includeUserFields) => {
  const userCreatedAtLocal = timestampToLocalDate(userData.createdAt);
  const firstSessionAtLocal = workouts.length > 0 ? timestampToLocalDate(workouts[0].createdAt) : null;
  const workoutRows = workouts.map((workoutData, workoutIndex) =>
    generateWorkoutRow(userData, workoutData, workoutIndex + 1, userCreatedAtLocal, firstSessionAtLocal, includeUserFields)
  );
  return workoutRows;
};

const generateUserQuestionnaireRows = (userData, questionnaires) => {
  const questionnaireRows = [];

  const preQuestionnaires = questionnaires.filter(q => q.phase === Phase.PRE);
  if (preQuestionnaires.length > 0)
    questionnaireRows.push(generateUserQuestionnaireRow(userData, Phase.PRE, preQuestionnaires));

  const postQuestionnaires = questionnaires.filter(q => q.phase === Phase.POST);
  if (postQuestionnaires.length > 0)
    questionnaireRows.push(generateUserQuestionnaireRow(userData, Phase.POST, postQuestionnaires));

  return questionnaireRows;
};

const generateUserQuestionnaireRow = (userData, phase, phaseQuestionnaires) => {
  const rowCells = QuestionnaireExcelConfig.columns.map((column) => {
    if (column.isUserField)
      return getUserFieldCell(column.field, userData, {}, phase, {});
    else if (column.field === "phase")
      return { value: phase.toUpperCase() };
  });

  rowCells.push(...generateQuestionnaireAnswerCells(phaseQuestionnaires));
  return rowCells;
};

const generateQuestionnaireAnswerCells = (phaseQuestionnaires) => {
  const cells = [];
  for (const questionnaire of QuestionnaireExcelConfig.questionnaires) {
    if (questionnaire.ignoreFetch) continue;

    const questionCount = questionnaire.questionCountForFetch || questionnaire.questionCount;
    const answers = Array(questionCount).fill().map(() => ({ value: null }));
    const filledQuestionnaire = phaseQuestionnaires.find(q => q.questionnaireId === questionnaire.id);
    if (filledQuestionnaire) {
      for (let i = 0; i < answers.length; i++) {
        answers[i].value = filledQuestionnaire.answers[i];
      }
    }
    cells.push(...answers);
  }
  return cells;
};

const generateUserEvalRow = (userData, evalData, context, phase) => {
  const rowCells = PatientExcelConfig.columns.map((column) => {
    if (column.isWorkoutStat)
      return getWorkoutStatCell(column.field, context.workoutStats);
    else
      return getUserFieldCell(column.field, userData, evalData, phase, context);
  });

  if (context.questionnaires !== null) {
    const phaseQuestionnaires = context.questionnaires.filter(q => q.phase === phase);
    rowCells.push(...generateQuestionnaireAnswerCells(phaseQuestionnaires));
  }

  rowCells.push(...PatientExcelConfig.placeholderColumns.map(() => ({ value: null })));
  return rowCells;
};

const generateWorkoutRow = (userData, workoutData, sessionNumber, userCreatedAtLocal, firstSessionAtLocal, includeUserFields) => {
  return getWorkoutColumns(includeUserFields).map((column) => {
    if (column.isUserField)
      return getUserFieldCell(column.field, userData, {}, Phase.PRE, {});
    else
      return getWorkoutFieldCell(column.field, workoutData, sessionNumber, userCreatedAtLocal, firstSessionAtLocal);
  });
};

const generateUserColumns = (includeQuestionnaires) => {
  return getUserColumnTitles(includeQuestionnaires).map((title) => ({ width: title.length }));
};

const generateUserHeaderRow = (includeQuestionnaires) => {
  return getUserColumnTitles(includeQuestionnaires).map((title) => ({
    value: title,
    fontWeight: "bold",
  }));
};

const getUserColumnTitles = (includeQuestionnaires) => {
  const answerTitles = includeQuestionnaires ? getQuestionnaireAnswerColumns().map((column) => column.title) : [];
  return [
    ...PatientExcelConfig.columns.map((column) => column.title),
    ...answerTitles,
    ...PatientExcelConfig.placeholderColumns,
  ];
};

const generateWorkoutColumns = (includeUserFields) => {
  return getWorkoutColumns(includeUserFields).map((column) => ({ width: column.title.length }));
};

const generateWorkoutsHeaderRow = (includeUserFields) => {
  return getWorkoutColumns(includeUserFields).map((column) => ({
    value: column.title,
    fontWeight: "bold",
  }));
};

const generateQuestionnaireColumns = () => {
  return getQuestionnaireColumns().map((column) => ({ width: column.title.length }));
};

const generateQuestionnairesHeaderRow = () => {
  return getQuestionnaireColumns().map((column) => ({
    value: column.title,
    fontWeight: "bold",
  }));
};

const getWorkoutColumns = (includeUserFields) => {
  return WorkoutExcelConfig.columns.filter(column => !column.isUserField || column.isUserField === includeUserFields);
};

const getQuestionnaireColumns = () => {
  return [...QuestionnaireExcelConfig.columns, ...getQuestionnaireAnswerColumns()];
};

const getQuestionnaireAnswerColumns = () => {
  const columns = [];
  QuestionnaireExcelConfig.questionnaires.forEach((questionnaire) => {
    for (let i = 1; i <= questionnaire.questionCount; i++) {
      const title = `${questionnaire.id}_${i}`;
      columns.push({ title: title, field: title });
    }
  });
  return columns;
};

const getUserFieldCell = (field, userData, evalData, phase, context) => {
  const evalDate = evalData.createdAt || evalData.updatedAt;
  switch (field) {
    case "hospitalName":
      return { value: getHospitalName(userData.hospital) };
    case "patientHspId":
      return { value: getPatientHspId(userData) };
    case "phase":
      return { value: phase.toUpperCase() };
    case "role":
      return { value: getPatientGroupName(userData.role) };
    case "evaluationDate":
      return { value: toExcelDay(evalDate.toDate()), type: Date, format: "dd/mm/yyyy" };
    case "dateOfBirth":
      return { value: userData.dateOfBirth.toDate(), type: Date, format: "dd/mm/yyyy" };
    case "diseaseOnsetDate":
      return { value: evalData.diseaseOnsetDate.toDate(), type: Date, format: "dd/mm/yyyy" };
    case "ageGroup":
      return { value: evalData.age < 13 ? "Niño" : "Adolescente" };
    case "diabetesDuration":
      const durationInMillis = evalDate.toDate() - evalData.diseaseOnsetDate.toDate();
      const durationInYears = durationInMillis / (1000 * 60 * 60 * 24 * 365.25);
      return { value: Math.round(durationInYears * 100) / 100, type: Number };
    case "countdownStartedAt":
      if (!userData.countdownStartedAt) return { value: null };
      return { value: toExcelDay(userData.countdownStartedAt.toDate()), type: Date, format: "dd/mm/yyyy" };
    case "postEvalDate":
      if (!userData.countdownStartedAt) return { value: null };
      return { value: toExcelDay(calculatePostEvalDate(userData.countdownStartedAt)), type: Date, format: "dd/mm/yyyy" };
    case "evalCreatedByName":
      // Evaluations saved before createdBy was recorded fall back to the patient's assigned doctor.
      if (!evalData.createdBy) return { value: userData.doctorName || null };
      return { value: evalData.evalCreatedByName || null };
    case "handgripZ":
      // Linear conversion requested by the client: P50 → 0, P0 → -1, P100 → +1.
      if (!(evalData.percentile > 0)) return { value: null };
      return { value: roundTo2((evalData.percentile - 50) / 50), type: Number };
    case "bmi":
      const heightInMeters = (evalData.height || userData.height) / 100;
      const weight = evalData.weight || userData.weight;
      if (!(heightInMeters > 0) || !(weight > 0)) return { value: null };
      return { value: roundTo2(weight / (heightInMeters * heightInMeters)), type: Number };
    case "hasIncidents":
      return { value: context.hasIncidents ? "Sí" : "No" };
    case "deviceModels":
      return { value: context.userDevices.models.join("\n") };
    case "devicePlatforms":
      return { value: context.userDevices.platforms.join("\n") };
    case "dominantHand":
      return { value: evalData.dominantHand === "R" ? "Derecha" : "Izquierda" };
    case "fitnessLevel":
      return { value: FitnessLevel[userData.fitnessLevel] };
    default:
      let value = evalData[field] || userData[field];
      if (typeof value === "boolean") {
        value = value ? "Sí" : "No";
      }
      else if (value === ManualEntry) {
        const customField = "custom" + field.charAt(0).toUpperCase() + field.slice(1);
        value = evalData[customField] || "Otro";
      }
      else if (typeof value === "number") {
        return value === -1 ? { value: "" } : { value, type: Number };
      }
      return { value };
  }
};

const getWorkoutStatCell = (field, workoutStats) => {
  let value = workoutStats[field];
  if (typeof value === "number") {
    return value === -1 ? { value: "" } : { value, type: Number };
  }
  return { value };
};

const getWorkoutFieldCell = (field, workoutData, sessionNumber, userCreatedAtLocal, firstSessionAtLocal) => {
  const createdAtLocal = timestampToLocalDate(workoutData.createdAt);
  const exercises = appendExerciseNames(workoutData.exercises);

  switch (field) {
    case "sessionDate":
      return { value: createdAtLocal, type: Date, format: "dd/mm/yyyy" };
    case "sessionTime":
      return { value: createdAtLocal, type: Date, format: "HH:MM" };
    case "weeksSinceRegistration":
      return { value: getSessionWeekNumber(userCreatedAtLocal, createdAtLocal), type: Number };
    case "weeksSinceFirstSession":
      return { value: getSessionWeekNumber(firstSessionAtLocal, createdAtLocal), type: Number };
    case "workoutType":
      return { value: WorkoutType[workoutData.workoutType] };
    case "avatarCount":
      return { value: AvatarCount[workoutData.avatarCount] };
    case "exercises[0]":
    case "exercises[1]":
    case "exercises[2]":
    case "exercises[3]":
    case "exercises[4]":
    case "exercises[5]":
      return { value: eval(field) };
    case "trendArrowPre":
      return { value: getArrowString(workoutData.trendArrowType, workoutData.trendArrowPre) };
    case "trendArrowPost":
      return { value: getArrowString(workoutData.trendArrowType, workoutData.trendArrowPost) };
    case "trendArrowType":
      return { value: getArrowTypeName(workoutData.trendArrowType) };
    case "rounds":
      // Tabata stores the total number of work intervals across all exercises; other types store rounds
      // per exercise. Export Tabata per exercise too so the column means the same for every type.
      if (typeof workoutData.rounds !== "number" || workoutData.rounds === -1) return { value: "" };
      if (WorkoutType[workoutData.workoutType] === "Tabata" && workoutData.exercises.length > 0)
        return { value: workoutData.rounds / workoutData.exercises.length, type: Number };
      return { value: workoutData.rounds, type: Number };
    case "exerciseCount":
      return { value: workoutData.exercises.length, type: Number };
    case "fitnessLevel":
      const fitnessLevel = workoutData.fitnessLevel || 0;
      return { value: FitnessLevel[fitnessLevel] };
    case "sessionNumber":
      return { value: sessionNumber, type: Number };
    default:
      let value = workoutData[field];
      if (typeof value === "number") {
        return value === -1 ? { value: "" } : { value, type: Number };
      }
      return { value };
  }
};

const appendExerciseNames = (exercises) => {
  return exercises.map((exerciseNumberString) => {
    const exerciseNumber = parseInt(exerciseNumberString);
    const exerciseName = exercisesJson[exerciseNumber].name;
    return `${exerciseNumberString}: ${exerciseName}`;
  });
};

const roundTo2 = (value) => Math.round(value * 100) / 100;

// write-excel-file converts Dates using UTC, so pass the local calendar day as UTC midnight
// to get a whole-day cell that shows the same date the user saw in the app.
const toExcelDay = (date) => new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
